import { useState, type FormEvent } from "react";
import { Send } from "lucide-react";
import {
  isMessageIdentifierGuardError,
  messageErrorMessage,
  MESSAGES_SAFETY_LINE,
} from "@/lib/messages";

export function MessageComposer({
  onSend,
  label = "Message",
  placeholder = "Write a message…",
  submitLabel = "Send message",
  autoFocus = false,
}: {
  onSend: (body: string) => Promise<void>;
  label?: string;
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
}) {
  const [body, setBody] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const identifierError = isMessageIdentifierGuardError(error);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || pending) return;
    setPending(true);
    setError(null);
    try {
      await onSend(trimmed);
      setBody("");
    } catch (sendError) {
      setError(sendError);
    } finally {
      setPending(false);
    }
  };

  return (
    <form className="pb-messages-composer" onSubmit={(event) => void submit(event)}>
      <label htmlFor="pb-message-body">{label}</label>
      <textarea
        id="pb-message-body"
        required
        maxLength={4000}
        value={body}
        placeholder={placeholder}
        autoFocus={autoFocus}
        aria-describedby={identifierError ? "pb-message-identifier-error" : "pb-message-safety"}
        onChange={(event) => {
          setBody(event.target.value);
          if (error) setError(null);
        }}
      />
      {identifierError ? (
        <p
          className="pb-messages-identifier-error"
          id="pb-message-identifier-error"
          role="alert"
        >
          Remove patient-identifying details before sending this message.
        </p>
      ) : error ? (
        <p className="pb-messages-form-error" role="alert">
          {messageErrorMessage(error)}
        </p>
      ) : null}
      <p className="pb-messages-safety" id="pb-message-safety">
        {MESSAGES_SAFETY_LINE}
      </p>
      <footer>
        <span>{body.length.toLocaleString("en-AU")} / 4,000 · Enter starts a new line</span>
        <button type="submit" disabled={pending || !body.trim()}>
          <Send size={14} aria-hidden="true" />
          {pending ? "Sending…" : submitLabel}
        </button>
      </footer>
    </form>
  );
}
